import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Notice } from '../ui/controls'
import { Band } from '../ui/Band'
import type { MessageKey } from '../i18n/messages'
import type { MatchingCount } from '../api/types'

const UNLOCK_PRODUCT = 'MATCH_UNLOCK'

/**
 * L1 — the free layer.
 *
 * It says **how many** people fit and **how well the next one does**, and it never says who. That
 * is not a limitation of this screen; it is what is being sold. 契约 §10.1 takes no candidate id
 * for the same reason: the queue is recomputed at the moment the button is pressed, so there is
 * no "that one" to point at.
 *
 * No exact score appears anywhere — 决议 V1.2 §3.3 keeps numbers off every layer, including the
 * paid report, so that MATCH_V2 can retune without every user noticing 88 became 86.
 */
export function MatchingPage() {
  const { t } = useI18n()

  const [count, setCount] = useState<MatchingCount | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.matching.count().then(setCount).catch(setError)
  }, [])

  if (!count) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  const blocked =
    count.blockedReason && count.blockedReason !== 'None' ? count.blockedReason : null

  const startUnlock = () => {
    setError(null)
    setBusy(true)

    // Freeze the queue position, then buy it, then go and pay. The idempotency key is generated
    // once per attempt so that a retry of a dropped response reaches the same order rather than
    // creating a second one (契约 §10.1).
    const idempotencyKey = crypto.randomUUID()

    api.matching
      .unlockNext()
      .then((episode) => api.orders.create(episode.matchId, UNLOCK_PRODUCT, idempotencyKey))
      .then((order) => api.orders.paymentSession(order.orderId))
      .then((session) => {
        // A full navigation, not fetch: the payment page belongs to the provider.
        window.location.assign(session.redirectUrl)
      })
      .catch((cause) => {
        setError(cause)
        setBusy(false)
      })
  }

  return (
    <div className="page stack">
      <h1>{t('matching.title')}</h1>

      <ErrorBanner error={error} />

      {blocked ? (
        <>
          <Notice>{t(`matching.blocked.${blocked}` as MessageKey)}</Notice>
          <div className="row-links">
            <Link to="/profile">{t('nav.profile')}</Link>
            <Link to="/preferences">{t('nav.preferences')}</Link>
            {blocked === 'InActiveConnection' ? (
              <Link to="/connection">{t('nav.connection')}</Link>
            ) : null}
          </div>
        </>
      ) : null}

      <section className="card stack">
        <div className="kv">
          <span>{t('matching.unlockable')}</span>
          <strong className="big-number">{count.unlockableCount}</strong>
        </div>

        {count.pendingInvitationCount > 0 ? (
          <div className="kv">
            <span>{t('matching.pendingInvites')}</span>
            <strong>
              <Link to="/invitations">{count.pendingInvitationCount}</Link>
            </strong>
          </div>
        ) : null}

        {count.distribution.length > 0 ? (
          <ul className="distribution">
            {count.distribution.map((row) => (
              <li key={row.band}>
                <Band value={row.band} /> <span>{row.count}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {count.unlockableCount === 0 && !blocked ? <Notice>{t('matching.noCandidates')}</Notice> : null}

      {count.nextBand ? (
        <section className="card stack">
          <div className="kv">
            <span>{t('matching.next')}</span>
            <strong>
              <Band value={count.nextBand} />
            </strong>
          </div>

          {count.nextFitDimensions.length > 0 ? (
            <div>
              <p className="field-hint">{t('matching.nextDimensions')}</p>
              <div className="chips">
                {count.nextFitDimensions.map((dimension) => (
                  <span className="chip" key={dimension}>
                    {dimension}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <p className="field-hint">{t('matching.neverWho')}</p>

          <button type="button" className="primary" disabled={busy} onClick={startUnlock}>
            {busy ? t('payment.redirecting') : t('matching.unlockNext')}
          </button>

          <p className="field-hint">{t('matching.price')}</p>
        </section>
      ) : null}
    </div>
  )
}
